import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, BarChart3, Check, Info, Wrench } from 'lucide-react';
import {
  ladeAnliegen,
  mitteilungGesehen,
  stimmeAb,
  type Mitteilung,
  type Umfrage,
} from '@/api/anliegen.api';
import { useAuthStore } from '@/store/useAuthStore';

// ─── Was beim Laden im Weg steht ─────────────────────────────────────────────
//
// Mitteilungen des Betreibers und laufende Abstimmungen. Beides erscheint
// beim Laden der Seite, eines nach dem anderen, und verschwindet, sobald es
// erledigt ist.
//
// Absichtlich im Weg – das ist der Unterschied zur E-Mail, die in einem
// Postfach landet, das niemand öffnet. Und absichtlich schnell erledigt: ein
// Klick je Mitteilung, ein Klick je Abstimmung. Was länger dauert, wird nicht
// gelesen, sondern weggeklickt.
//
// Eine Abstimmung lässt sich überspringen, eine Mitteilung nicht. Die Frage
// ist ein Angebot; der Hinweis auf die Wartung am Sonntag ist keines.

const SYMBOLE: Record<string, typeof Info> = {
  info: Info,
  warnung: AlertTriangle,
  wartung: Wrench,
};

const FARBEN: Record<string, string> = {
  info: 'text-blue-600 bg-blue-50',
  warnung: 'text-amber-700 bg-amber-50',
  wartung: 'text-violet-700 bg-violet-50',
};

export function AnliegenPopup() {
  const queryClient = useQueryClient();
  const angemeldet = useAuthStore((s) => Boolean(s.user));

  const { data } = useQuery({
    queryKey: ['anliegen'],
    queryFn: ladeAnliegen,
    enabled: angemeldet,
    // Eine Begrüßung, kein Live-Zustand: einmal je Sitzung reicht. Ein
    // Nachladen mitten im Lesen ließe den Kasten wechseln.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  /** Was gerade gezeigt wird – erst Mitteilungen, dann Abstimmungen. */
  const [erledigt, setErledigt] = useState<string[]>([]);

  const offen = [
    ...(data?.mitteilungen ?? []).map((m) => ({ art: 'mitteilung' as const, m })),
    ...(data?.umfragen ?? []).map((u) => ({ art: 'umfrage' as const, u })),
  ].filter((x) => !erledigt.includes(x.art === 'mitteilung' ? x.m.id : x.u.id));

  const aktuell = offen[0];

  const bestaetigen = useMutation({
    mutationFn: mitteilungGesehen,
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['anliegen'] }),
  });

  // Escape schließt nur, was sich schließen lässt: eine Abstimmung. Eine
  // Mitteilung braucht den Klick – sonst wäre sie beim nächsten Laden wieder
  // da, und das ärgert mehr, als es nützt.
  useEffect(() => {
    if (!aktuell || aktuell.art !== 'umfrage') return;
    const taste = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setErledigt((alt) => [...alt, aktuell.u.id]);
    };
    window.addEventListener('keydown', taste);
    return () => window.removeEventListener('keydown', taste);
  }, [aktuell]);

  if (!aktuell) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[1900] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={aktuell.art === 'mitteilung' ? aktuell.m.titel : aktuell.u.frage}
    >
      <div className="absolute inset-0 bg-slate-900/50" aria-hidden="true" />

      <div className="relative w-full max-w-md rounded-xl bg-white p-5 shadow-2xl">
        {aktuell.art === 'mitteilung' ? (
          <MitteilungInhalt
            mitteilung={aktuell.m}
            offenGesamt={offen.length}
            onBestaetigen={() => {
              bestaetigen.mutate(aktuell.m.id);
              setErledigt((alt) => [...alt, aktuell.m.id]);
            }}
          />
        ) : (
          <UmfrageInhalt
            umfrage={aktuell.u}
            offenGesamt={offen.length}
            onFertig={() => setErledigt((alt) => [...alt, aktuell.u.id])}
          />
        )}
      </div>
    </div>,
    document.body,
  );
}

function Zaehler({ offen }: { offen: number }) {
  if (offen <= 1) return null;
  return (
    <p className="mt-3 text-center text-xs text-slate-400">
      Noch {offen - 1} {offen - 1 === 1 ? 'weiterer Hinweis' : 'weitere Hinweise'}
    </p>
  );
}

function MitteilungInhalt({
  mitteilung,
  offenGesamt,
  onBestaetigen,
}: {
  mitteilung: Mitteilung;
  offenGesamt: number;
  onBestaetigen: () => void;
}) {
  const Symbol = SYMBOLE[mitteilung.art] ?? Info;
  const farbe = FARBEN[mitteilung.art] ?? FARBEN.info;

  return (
    <>
      <div className="flex items-start gap-3">
        <span className={`rounded-lg p-2 ${farbe}`}>
          <Symbol className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold text-slate-900">{mitteilung.titel}</h2>
          <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-600">
            {mitteilung.text}
          </p>
        </div>
      </div>

      <button
        onClick={onBestaetigen}
        className="mt-5 w-full rounded-md bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
      >
        Verstanden
      </button>
      <Zaehler offen={offenGesamt} />
    </>
  );
}

function UmfrageInhalt({
  umfrage,
  offenGesamt,
  onFertig,
}: {
  umfrage: Umfrage;
  offenGesamt: number;
  onFertig: () => void;
}) {
  const queryClient = useQueryClient();
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const [ergebnis, setErgebnis] = useState<Umfrage | null>(null);

  const abstimmen = useMutation({
    mutationFn: (optionId: string) => stimmeAb(umfrage.id, optionId),
    onSuccess: (u) => {
      setErgebnis(u);
      queryClient.invalidateQueries({ queryKey: ['anliegen'] });
      queryClient.invalidateQueries({ queryKey: ['umfragen'] });
    },
  });

  // Nach dem Abstimmen das Ergebnis – dafür macht man mit.
  if (ergebnis) {
    return (
      <>
        <h2 className="font-semibold text-slate-900">Danke für Ihre Stimme</h2>
        <ul className="mt-4 space-y-2.5">
          {ergebnis.optionen.map((o) => {
            const anteil = ergebnis.gesamt > 0 ? (o.stimmen / ergebnis.gesamt) * 100 : 0;
            return (
              <li key={o.id}>
                <div className="flex items-baseline justify-between gap-2 text-sm">
                  <span className="min-w-0 text-slate-700">{o.text}</span>
                  <span className="shrink-0 tabular-nums text-xs text-slate-500">
                    {Math.round(anteil)} %
                  </span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full bg-blue-600" style={{ width: `${anteil}%` }} />
                </div>
              </li>
            );
          })}
        </ul>
        <p className="mt-3 text-xs text-slate-400">
          {ergebnis.gesamt} {ergebnis.gesamt === 1 ? 'Stimme' : 'Stimmen'} insgesamt
        </p>
        <button
          onClick={onFertig}
          className="mt-4 w-full rounded-md bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
        >
          Schließen
        </button>
      </>
    );
  }

  return (
    <>
      <div className="flex items-start gap-3">
        <span className="rounded-lg bg-blue-50 p-2 text-blue-600">
          <BarChart3 className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold text-slate-900">{umfrage.frage}</h2>
          {umfrage.text && <p className="mt-1 text-sm text-slate-600">{umfrage.text}</p>}
        </div>
      </div>

      <div className="mt-4 space-y-2">
        {umfrage.optionen.map((o) => (
          <button
            key={o.id}
            onClick={() => setGewaehlt(o.id)}
            className={`flex w-full items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left text-sm ${
              gewaehlt === o.id
                ? 'border-blue-500 bg-blue-50 text-blue-900'
                : 'border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
          >
            <span
              className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                gewaehlt === o.id ? 'border-blue-600 bg-blue-600' : 'border-slate-300'
              }`}
              aria-hidden="true"
            >
              {gewaehlt === o.id && <Check className="h-2.5 w-2.5 text-white" />}
            </span>
            {o.text}
          </button>
        ))}
      </div>

      <p className="mt-3 text-xs text-slate-400">
        Ihre Antwort ist anonym. Gespeichert wird, dass Sie abgestimmt haben – nicht wofür.
        Sie lässt sich deshalb nicht zurücknehmen.
      </p>

      <button
        onClick={() => gewaehlt && abstimmen.mutate(gewaehlt)}
        disabled={!gewaehlt || abstimmen.isPending}
        className="mt-4 w-full rounded-md bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
      >
        Abstimmen
      </button>
      <button
        onClick={onFertig}
        className="mt-2 w-full text-center text-xs text-slate-400 hover:text-slate-600"
      >
        Später
      </button>
      <Zaehler offen={offenGesamt} />
    </>
  );
}
